import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import {AlertTitle} from '@chakra-ui/react';

import {AlertIcon} from '@amblique/king-living/app/components/icons';
import {ProductDetail as ProductDetailComponent} from '@amblique/king-living/app/components/product-detail';
import {ProductDetailContentfulContext} from '@amblique/king-living/app/context/product-detail-contentful-context';
import {usePageContext} from '@amblique/king-living/app/hooks/contentful';
import {usePixleeScript} from '@amblique/king-living/app/hooks/use-pixlee-script';
import Alert from '@amblique/king-living/app/theme/components/base/alert';

/**
 * @override
 * ProductDetail Contentful model component.
 *
 * Wraps the SFCC `ProductDetailComponent` with a `ProductDetailContentfulContext`
 * provider so that any descendant (e.g. `ChooseYourSets`) can access the
 * Contentful entry data — including `customizeContent` and
 * `customizeImageBackground` — without prop drilling.
 *
 * The `component` value passed to the provider is the full Contentful entry
 * after the lazy-fetch completes (managed by the Component wrapper in
 * pwa-contentful). While `isFetching` is true the context value may be
 * incomplete; consumers should guard accordingly.
 *
 * @param {Object} props - Component props
 * @param {Contentful.ProductDetail} props.component - ProductDetail component data
 * @param {boolean} [props.isComplete] - Whether component has all fields
 * @param {boolean} [props.isFetching] - Whether additional data is being fetched
 * @returns {React.ReactElement}
 */
export const ProductDetail = ({component, isComplete = true, isFetching = false, ...props}) => {
  const {
    isEdit,
    isProductPage,

    product,
    isProductLoading,
    isProductError,
    productError,
  } = usePageContext();

  // Load Pixlee script for UGC widgets on product pages
  usePixleeScript();

  return (
    isProductPage ? (
      // Pass full Contentful entry to context provider so any descendant can access it
      // without prop drilling through ProductDetailComponent.
      <ProductDetailContentfulContext.Provider value={component}>
        <ProductDetailComponent
          isProductError={isProductError}
          isProductLoading={isProductLoading}
          product={product}
          productError={productError}
        />
      </ProductDetailContentfulContext.Provider>
    ) : isEdit ? (
      <Alert status="warning">
        <AlertIcon />
        <AlertTitle>Product Detail - Dynamic</AlertTitle>

        <FormattedMessage
          defaultMessage="This component can only be added to '{aspectType}' pages."
          id="contentful.error.invalid_aspects"
          values={{aspectType: 'Product Detail'}}
        />
      </Alert>
    ) : null
  );
};

ProductDetail.displayName = 'ProductDetail';
ProductDetail.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
